import {
  addDisplayName,
  addProps,
  addStateHandlers,
  addWrapper,
  branch,
  flowMax,
  returns,
} from 'ad-hok'
import { branchIfPassesPredicate } from 'ad-hok-utils'
import { FC } from 'react'
import {
  MediaQueue,
  MediaQueueItem,
  CurrentUser,
  isMediaQueueError,
} from 'shared/src/api/types'
import { typedAs } from 'shared/src/utils/typedAs'
import { addUser } from 'shared/src/api'
import { createUseStyles } from 'react-jss'
import { addOptionalIntPathParam } from 'shared/src/utils/addNavigate'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { SocialService } from '../../utils/social'
import Icon, { IconName } from '../Icon'
import { Span } from '../Text'
import { SocialLinkButton } from '../SocialLinkButton'
import { NOTES_MEDIUM_STYLE, NOTES_BOLD_STYLE } from '../styles'
import { Media } from './Media'
import { Button } from '../Button'
import { SIDE_PADDING } from '../PageConstants'

const MARGIN_BOTTOM = 48

const useStyles = createUseStyles({
  queuedImagesContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: MARGIN_BOTTOM,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    height: 48,
  },
  headerIcon: {
    color: '#CBA67A',
  },
  headerText: {
    ...NOTES_BOLD_STYLE,
    color: '#CBA67A',
    marginLeft: 16,
  },
  mediaContainer: {
    display: 'flex',
    overflowX: 'scroll',
    gap: 12,
    width: `calc(100% + ${SIDE_PADDING}px)`,
    paddingRight: SIDE_PADDING,
    paddingTop: 9,
  },
  button: {
    marginTop: 22,
  },
  socialLinkButton: {
    marginBottom: 12 - MARGIN_BOTTOM,
  },
  loadingPostsText: {
    ...NOTES_MEDIUM_STYLE,
    fontStyle: 'italic',
    marginTop: 24,
  },
})

const shouldShowLinkButtonConditions: Record<
  SocialService,
  (user: CurrentUser) => boolean
> = {
  instagram: (user) => !user.hasInstagramConnection,
  tikTok: (user) => !user.hasTikTokConnection,
}

const idFromMediaQueueItem = ({ sourceType, id }: MediaQueueItem): string =>
  `${sourceType}${id}`

interface HeaderProps {
  title: string
  icon: IconName
}

const Header: FC<HeaderProps> = flowMax(
  addDisplayName('Header'),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ icon, title, classes }) => (
    <div className={classes.header}>
      <Icon name={icon} className={classes.headerIcon} />
      <Span type="notes2" className={classes.headerText}>
        {title}
      </Span>
    </div>
  )
)

interface ContentProps {
  buttonTitle: string
  items: MediaQueueItem[]
  onSelect: (mediaQueueItem: MediaQueueItem) => void
  onDelete?: (mediaQueueItem: MediaQueueItem) => void
}

const Content: FC<ContentProps> = flowMax(
  addDisplayName('Content'),
  addStateHandlers(
    {
      selectedId: typedAs<string | null>(null),
    },
    {
      toggleSelectedId:
        ({ selectedId }) =>
        (item: MediaQueueItem) => {
          const id = idFromMediaQueueItem(item)

          return {
            selectedId: id === selectedId ? null : id,
          }
        },
    }
  ),
  addTranslationHelpers,
  addProps(({ selectedId, items }) => ({
    selectedMedia: items.find(
      (item) => idFromMediaQueueItem(item) === selectedId
    ),
  })),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    items,
    selectedId,
    selectedMedia,
    toggleSelectedId,
    onSelect,
    onDelete,
    buttonTitle,
    classes,
  }) => (
    <>
      <div className={classes.mediaContainer}>
        {items.map((item) => (
          <Media
            media={item}
            type={item.type}
            state={
              selectedMedia
                ? selectedMedia === item
                  ? 'selected'
                  : 'otherSelected'
                : 'normal'
            }
            onClick={() => {
              toggleSelectedId(item)
            }}
            onDelete={onDelete ? () => onDelete(item) : undefined}
            key={idFromMediaQueueItem(item)}
          />
        ))}
      </div>
      <Button
        text={buttonTitle}
        type="fullWidth"
        icon="next"
        showIconAfterText
        onClick={() => onSelect(selectedMedia!)}
        disabled={!selectedId}
        className={classes.button}
      />
    </>
  )
)

interface Props {
  service: SocialService
  mediaQueue: MediaQueue
  onSelect: (mediaQueueItem: MediaQueueItem) => void
  onDelete: (mediaQueueItem: MediaQueueItem) => void
  isLast: boolean
}

export const SocialSection: FC<Props> = flowMax(
  addDisplayName('SocialSection'),
  addProps(() => ({
    classes: useStyles(),
  })),
  addWrapper((render, { classes }) => (
    <div className={classes.container}>{render()}</div>
  )),
  addUser,
  addOptionalIntPathParam('userId'),
  branch(
    ({ service, user, userId }) =>
      !userId && shouldShowLinkButtonConditions[service](user),
    returns(({ service, classes, isLast }) => (
      <SocialLinkButton
        service={service}
        className={!isLast ? classes.socialLinkButton : undefined}
      />
    ))
  ),
  addTranslationHelpers,
  addProps(({ service, t }) => ({
    icon: typedAs<IconName>(service),
    title: t(`admin.mediaQueue.${service}`),
  })),
  addWrapper((render, { title, icon }) => (
    <>
      <Header title={title} icon={icon} />
      {render()}
    </>
  )),
  addProps(({ service, mediaQueue }) => ({
    source: mediaQueue[service],
  })),
  branchIfPassesPredicate('source', isMediaQueueError, {
    returns: ({ source }) => <div>{source.error}</div>,
  }),
  addProps(({ service, t }) => ({
    buttonTitle: t(`admin.mediaQueue.${service}Button`),
  })),
  ({
    buttonTitle,
    service,
    source: { items },
    onSelect,
    onDelete,
    classes,
    t,
  }) => (
    <div>
      {service === 'instagram' && items.length === 0 && (
        <div className={classes.loadingPostsText}>
          {t('admin.mediaQueue.socialPostsNotReady')}
        </div>
      )}
      <Content
        buttonTitle={buttonTitle}
        items={items}
        onSelect={onSelect}
        onDelete={onDelete}
      />
    </div>
  )
)

interface QueuedImagesProps {
  images: MediaQueueItem[]
  onSelect: (mediaQueueItem: MediaQueueItem) => void
  onDelete: (mediaQueueItem: MediaQueueItem) => void
}

export const QueuedImages: FC<QueuedImagesProps> = flowMax(
  addDisplayName('QueuedImages'),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({ images, onSelect, onDelete, classes, t }) => (
    <div className={classes.queuedImagesContainer}>
      <Header title={t('admin.mediaQueue.imagesTitle')} icon="queuedImages" />
      <Content
        buttonTitle={t('admin.mediaQueue.imagesButton')}
        items={images}
        onSelect={onSelect}
        onDelete={onDelete}
      />
    </div>
  )
)
